import { useMemo } from 'react';

import { SendMoneyFlowModal } from '@/widgets/widgets-slider';
import { useSendMoney } from '@/widgets/widgets-slider/model/useSendMoney';
import { useSerializePaymentToForm } from '@/widgets/widgets-slider/ui/cards/send-money-shared/lib';

import { usePaymentsController } from '@/entities/payment/model';
import { IPaymentTemplate } from '@/entities/payment/lib/serialize';

import { VabsParticipantType, VabsPaymentSystem } from '@/shared/api';
import { Human, SendIcon } from '@/shared/assets';
import { openModal } from '@/shared/lib';
import { shortText } from '@/shared/lib/utils/text';
import { AvatarWithIndicator, Header, Skeleton, Typography } from '@/shared/ui';
import { useShallow } from 'zustand/react/shallow';

import { FavoritesModal } from '@/pages/Payments/ui/FavoritesModal';
import { useNavigate } from 'react-router';

const getPaymentName = (payment: IPaymentTemplate) => {
    if (payment.paymentSystem === VabsPaymentSystem.Inner) return payment.name || '';
    if (payment.type === VabsParticipantType.B2C) {
        return `${payment.firstName || ''} ${payment.lastName || ''}`.trim();
    }
    return payment.companyName || '';
};

const getPaymentSubline = (payment: IPaymentTemplate) => {
    if (payment.paymentSystem === VabsPaymentSystem.Inner) return 'FCB account';
    return `${payment.currency?.ticker || ''} account: ${shortText(payment.accountNumber || '', 6)}`;
};

const FavoriteItem = ({
    payment,
    onClick,
}: {
    payment: IPaymentTemplate;
    onClick?: () => void;
}) => {
    return (
        <button
            type="button"
            className="flex w-full items-center gap-3 py-2 text-left bg-transparent"
            onClick={onClick}
        >
            <AvatarWithIndicator
                className="pr-1"
                indicator={<SendIcon className="h-3 w-3" />}
                defaultAvatar={
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-base-300">
                        <Human className="!h-6 !w-6" />
                    </div>
                }
            />
            <div className="flex min-w-0 flex-col gap-1">
                <Typography className="truncate">{shortText(getPaymentName(payment), 32)}</Typography>
                <Typography variant="14-r" color="text-base-100" className="truncate">
                    {getPaymentSubline(payment)}
                </Typography>
            </div>
        </button>
    );
};

const openFavoritesModal = () => {
    openModal({ id: 'favorites-modal', component: FavoritesModal });
};

const FAVORITES_LIMIT = 4

export const FavoritesWidget = () => {
    const { favorites, isFavoritesLoading } = usePaymentsController(true);
    const navigate = useNavigate()
    const { updateFormData, setStep, resetData } = useSendMoney(
        useShallow((state) => state),
    );
    const { serialize } = useSerializePaymentToForm();

    const handleOpenPayment = (payment: IPaymentTemplate) => {
        resetData();
        if (payment.paymentSystem === VabsPaymentSystem.Inner) {
            setStep('amount');
        }
        updateFormData({
            ...serialize(payment),
        });
    };

    const headerAction = useMemo(() => {
        if (!favorites?.length) {
            return { as: 'button' as const, label: 'Add +', onClick: openFavoritesModal };
        }

        if (favorites.length > FAVORITES_LIMIT) {
            return {
                as: 'button' as const,
                label: 'See all',
                onClick: () => navigate('/payments', { state: { focusFavorites: true } }),
            };
        }

        return { as: 'button' as const, label: 'Edit', onClick: openFavoritesModal };
    }, [favorites, navigate]);

    return (
        <div className="flex h-full w-full flex-col">
            <Header className="pb-6" title="Favourite payments" action={headerAction} />

            <div className="flex h-full flex-col gap-y-3">
                {isFavoritesLoading ? (
                    <div className="flex flex-col gap-4 p-4">
                        {Array.from({ length: 4 }).map((_, idx) => (
                            <Skeleton key={idx} className="h-10 rounded-xl" />
                        ))}
                    </div>
                ) : !favorites?.length ? (
                    <div className="my-auto flex h-full w-full flex-col items-center justify-center gap-1 text-center text-base-100">
                        <p>No favourite payments added yet</p>
                    </div>
                ) : (
                    favorites.slice(0, 4).map((payment) => (
                        <SendMoneyFlowModal
                            key={payment.id}
                            defaultStep={
                                payment.paymentSystem === VabsPaymentSystem.Inner
                                    ? 'send-inner'
                                    : payment.type === VabsParticipantType.B2C
                                        ? 'send-someone'
                                        : 'send-business'
                            }
                        >
                            <FavoriteItem
                                payment={payment}
                                onClick={() => {
                                    handleOpenPayment(payment);
                                }}
                            />
                        </SendMoneyFlowModal>
                    ))
                )}
            </div>
        </div>
    );
};
